import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useFormDisable } from '../../../../../../shared/form-new/form'
import { FormNewDateSelect } from '../../../../../../shared/form-new/form-date-select'
import { FormNewSecret, FormNewTextArea } from './fields'
import { PemDrop } from '../../../../../../shared/sso-workspace/pem-drop'
import { CredentialCheckResult } from '../../../../../../shared/sso-workspace/credential-check'

// How EducationXR proves who it is to the identity provider: the OIDC
// credential part of the old OidcPanel. clientAuthMethod is a segmented
// control writing the form value; certificate is offered only when the
// issuer advertises private_key_jwt (or the row already uses it).
//
// Above the fields, the last health check's "Credential accepted" result:
// whether the identity provider actually accepts what is saved here.
// `health` is the connection's health row (undefined while it loads, null
// when it couldn't be read); showCheck is off in the add wizard, where
// nothing has been saved to check yet.
export const SectionCredentials = ({ discovery, health, showCheck = false }) => {
  if (!showCheck) return <CredentialFields discovery={discovery} />
  const check = health === undefined ? undefined : (health && health.credentialCheck) || null
  return (
    <div className="d-grid gap-3">
      <CredentialCheckResult check={check} />
      <CredentialFields discovery={discovery} />
    </div>
  )
}

const CredentialFields = ({ discovery }) => {
  const { watch, setValue } = useFormContext()
  const formDisabled = useFormDisable()
  const connection = watch('connection')
  const method = watch('clientAuthMethod')

  if (connection === 'shared_app') return <p className="sw-hint m-0">Credentials come from the platform configuration for this connection.</p>

  const certificateAllowed = Boolean(discovery && (discovery.authMethods || []).includes('private_key_jwt')) || method === 'private_key_jwt'
  const expiresRequired = method === 'client_secret'
  const pick = value => setValue('clientAuthMethod', value, { shouldDirty: true })

  return (
    <div className="row g-3">
      <div className="col-12 sw-field">
        <span className="sw-label">Method</span>
        <div className="btn-group align-self-start" role="group" aria-label="credential method">
          <button type="button" className="btn sw-btn" aria-pressed={method === 'client_secret'} disabled={formDisabled} onClick={() => pick('client_secret')}>Client secret</button>
          <button type="button" className="btn sw-btn" aria-pressed={method === 'private_key_jwt'} disabled={formDisabled || !certificateAllowed} onClick={() => pick('private_key_jwt')}>Certificate</button>
        </div>
        <div className="sw-hint">{certificateAllowed ? <React.Fragment>Certificate is available: the issuer advertises <code>private_key_jwt</code>.</React.Fragment> : 'Look up the issuer to see whether it accepts a certificate.'}</div>
      </div>
      {method === 'private_key_jwt'
        ? <React.Fragment>
            <div className="col-12">
              <PemDrop disabled={formDisabled}
                onKey={pem => setValue('privateKeyPEMNew', pem, { shouldDirty: true })}
                onCertificate={(pem, notAfter) => {
                  setValue('certificatePEM', pem, { shouldDirty: true })
                  // The certificate knows its own expiry: nothing to type.
                  if (notAfter) setValue('expiresAt', notAfter, { shouldDirty: true })
                }} />
            </div>
            <div className="col-12"><FormNewSecret name="privateKeyPEMNew" label="Private key" hasValue={watch('hasPrivateKey')} textarea /></div>
            <div className="col-12"><FormNewTextArea name="certificatePEM" label="Certificate" secret subtext="The public certificate: not secret, this round-trips on save." /></div>
          </React.Fragment>
        : <div className="col-md-6"><FormNewSecret name="clientSecretNew" label="Client secret" hasValue={watch('hasClientSecret')} /></div>}
      <div className="col-md-6"><FormNewDateSelect name="expiresAt" label="Expires" required={expiresRequired} /></div>
    </div>
  )
}
